import { gql, QueryResult } from '@apollo/client'
import * as React from 'react'
import styled from 'styled-components'
import Button from '../../../../common/src/components/Button'
import { Link } from '../../../../common/src/components/Link'
import Modal from '../../../../common/src/components/Modal'
import { IntroText, EllipsisDiv, BodyTextSpan, Spacer, FixedWidthSpan } from '../../../../common/src/components/core'
import expandable from '../../../../common/src/components/expandable'
import { Colors } from '../../../../common/src/constants'
import { TestIds } from '../../../../common/src/data/testids'
import { diffLongFmt } from '../../../../common/src/date'
import {
  SavedActionWithContactFragment,
  SavedActionWithTaskFragment,
  FetchLeasingTaskRecentActionsQuery,
  FetchLeasingTaskRecentActionsQueryVariables,
  FetchAgentRecentActionsQuery,
  FetchAgentRecentActionsQueryVariables,
} from '../../../../common/src/types.gen'
import { getTaskPath } from '../route'
import { PollingQuery } from './PollingQuery'

const savedActionWithTask = gql`
  fragment SavedActionWithTask on SavedAction {
    id
    timeCreated
    actionDisplayName
    user {
      id
      name
    }
    externalUserId
    description
    task {
      id
      displayName
    }
  }
`

const savedActionWithContact = gql`
  fragment SavedActionWithContact on SavedAction {
    id
    timeCreated
    actionDisplayName
    user {
      id
      name
    }
    externalUserId
    description
  }
`

export const fetchLeasingTaskRecentActionsQuery = gql`
  query FetchLeasingTaskRecentActions($taskId: Int!) {
    task(taskId: $taskId) {
      id
      recentActions(limit: 50) {
        ...SavedActionWithContact
      }
    }
  }
  ${savedActionWithContact}
`

export const fetchAgentRecentActionsQuery = gql`
  query FetchAgentRecentActions($contactId: Int!) {
    contact(contactId: $contactId) {
      id
      recentActions(limit: 10) {
        ...SavedActionWithTask
      }
    }
  }
  ${savedActionWithTask}
`

type SavedAction = SavedActionWithContactFragment | SavedActionWithTaskFragment

type TaskActionsResult = QueryResult<FetchLeasingTaskRecentActionsQuery, FetchLeasingTaskRecentActionsQueryVariables>
type AgentActionsResult = QueryResult<FetchAgentRecentActionsQuery, FetchAgentRecentActionsQueryVariables>
type ActionsResult = TaskActionsResult | AgentActionsResult

function ActionLogModal(props: ActionsResult & { onClose: () => void }) {
  const { data, onClose } = props
  const task = (data as FetchLeasingTaskRecentActionsQuery)?.task
  const contact = (data as FetchAgentRecentActionsQuery)?.contact
  const source = task || contact
  const recentActions = source?.recentActions

  return (
    <Modal title="Action log" width={960} onClose={onClose} data-testid={TestIds.actionLogModal}>
      {source == null && <IntroText>Loading...</IntroText>}
      {source != null && <SavedActions actions={recentActions} />}
    </Modal>
  )
}

function SavedActions({ actions }: { actions: SavedAction[] }) {
  return (
    <SavedActionsWrap>
      {actions.length === 0 && <NoActionsIntroText>No recorded changes yet</NoActionsIntroText>}
      {actions.map((res: SavedAction, i: number) => (
        <SavedActionRow key={i} {...res} />
      ))}
    </SavedActionsWrap>
  )
}

const NoActionsIntroText = styled(IntroText)`
  margin: 0 12px;
`

const SavedActionsWrap = styled.div`
  margin: 0 -12px;
`

export function SavedActionRow(props: SavedAction) {
  const { description, actionDisplayName, externalUserId, user, timeCreated } = props
  const { task } = props as SavedActionWithTaskFragment

  const updateUser = user ? user.name : 'PM Sync'
  const userText = externalUserId ? externalUserId : updateUser

  const today = new Date()
  const timeElapsed = diffLongFmt(today, timeCreated)
  const timeCreatedLocale = new Date(timeCreated).toLocaleString('en-US')

  return (
    <SavedActionPellet data-testid={TestIds.savedActionRow}>
      <IntroText color="snow">
        <EllipsisDiv>
          <BodyTextFixedWidthSpan width={180} center={false} color={'silver'}>
            <span title={timeCreatedLocale}>{`${timeElapsed} ago`}</span>
          </BodyTextFixedWidthSpan>
          <BodyTextSpan color={'silver'}>
            {`${actionDisplayName} by `}
            <ItalicsSpan>{userText}</ItalicsSpan>
          </BodyTextSpan>
          <Spacer height={4} />
          <SavedActionUpdates description={description} task={task} />
        </EllipsisDiv>
      </IntroText>
    </SavedActionPellet>
  )
}

const SavedActionPellet = styled.div`
  display: inline-block;
  width: 912px;
  padding: 12px;
`

const BodyTextFixedWidthSpan = styled(FixedWidthSpan)`
  font-size: 14px;
`

const ItalicsSpan = styled.span`
  font-style: italic;
`

function SavedActionUpdates(props: { description: string; task: SavedActionWithTaskFragment['task'] | null }) {
  const { description, task } = props

  return (
    <SavedActionUpdatesList>
      {task && (
        <SavedActionUpdate>
          {'task ∙ '}
          <Link to={getTaskPath(task.id)}>{task?.displayName}</Link>
        </SavedActionUpdate>
      )}
      <SavedActionUpdateDescription>{description}</SavedActionUpdateDescription>
    </SavedActionUpdatesList>
  )
}

const SavedActionUpdatesList = styled.ul`
  padding-left: 180px;
  width: 702px;
`

const SavedActionUpdate = styled.li`
  font-size: 14px;
  color: '${Colors.snow}';
  width: 702px;
  white-space: pre-line;
`

const SavedActionUpdateDescription = styled.pre`
  font-size: 14px;
  color: '${Colors.snow}';
  width: 702px;
  white-space: pre-wrap;
`

interface TaskActionLogProps {
  taskId: number
}

interface AgentActionLogProps {
  contactId: number
}

type ActionLogProps = TaskActionLogProps | AgentActionLogProps

interface ExpandableProps {
  isExpanded: boolean
  toggleExpand: () => void
}

function isTaskProps(props: ActionLogProps): props is TaskActionLogProps {
  return (props as TaskActionLogProps).taskId !== undefined
}

function ActionLogImpl(props: ActionLogProps & ExpandableProps) {
  if (!props.isExpanded) {
    return (
      <div>
        <Spacer height={32} />
        <Button onClick={props.toggleExpand} size="medium">
          Action log
        </Button>
      </div>
    )
  }

  if (isTaskProps(props)) {
    return (
      <PollingQuery query={fetchLeasingTaskRecentActionsQuery} variables={{ taskId: props.taskId }}>
        {(res: TaskActionsResult) => <ActionLogModal onClose={props.toggleExpand} {...res} />}
      </PollingQuery>
    )
  }
  return (
    <PollingQuery query={fetchAgentRecentActionsQuery} variables={{ contactId: props.contactId }}>
      {(res: AgentActionsResult) => <ActionLogModal onClose={props.toggleExpand} {...res} />}
    </PollingQuery>
  )
}

const ActionLogExpandable = expandable(ActionLogImpl) as React.ComponentType<ActionLogProps>

export default function ActionLog(props: ActionLogProps) {
  if (isTaskProps(props)) {
    return <ActionLogExpandable taskId={props.taskId} />
  }
  return <ActionLogExpandable contactId={props.contactId} />
}
